<template>
	<form class="form-base">
		<v-row>
			<v-col cols="3">
				<v-text-field
					v-model.trim="v.lastName.$model"
					:error-messages="getValidationErrorText(v.lastName)"
					:disabled="disabled"
					label="Фамилия"
					outlined
					@blur="v.lastName.$touch"
				/>
			</v-col>
			<v-col cols="3">
				<v-text-field
					v-model.trim="v.firstName.$model"
					:error-messages="getValidationErrorText(v.firstName)"
					:disabled="disabled"
					label="Имя"
					outlined
					@blur="v.firstName.$touch"
				/>
			</v-col>
			<v-col cols="3">
				<v-text-field
					v-model.trim="v.patronymic.$model"
					:error-messages="getValidationErrorText(v.patronymic)"
					:disabled="disabled"
					label="Отчество (при наличии)"
					outlined
					@blur="v.patronymic.$touch"
				/>
			</v-col>
			<v-col cols="3">
				<DateFieldCustom
					label="Дата рождения"
					:value="v.birthDate.$model"
					@change="v.birthDate.$model = $event"
					:errorMessage="v.birthDate"
					:disabled="disabled"
					:max="new Date().toISOString()"
					@blur="v.birthDate.$touch"
				/>
			</v-col>
			<v-col cols="4">
				<v-text-field
					v-model="v.snils.$model"
					:errorMessages="getValidationErrorText(v.snils)"
					:disabled="disabled"
					v-mask="['###-###-### ##']"
					label="СНИЛС"
					outlined
					@blur="v.snils.$touch"
				/>
			</v-col>
			<v-col cols="4">
				<v-text-field
					v-model="v.inn.$model"
					:errorMessages="getValidationErrorText(v.inn)"
					:disabled="disabled"
					v-mask="['############']"
					label="ИНН"
					outlined
					@blur="v.inn.$touch"
				/>
			</v-col>
			<v-col cols="12">
				<AddressStructuredField
					:value="v.address.$model"
					@change="v.address.$model = $event"
					:v="v.address"
					label="Место жительства или место пребывания "
					:notice-recovery="true"
					:global-disabled="disabled"
					@blur="v.address.$touch"
					:translationKey="translationKey ? `${translationKey}.addr` : undefined"
				/>
			</v-col>
			<v-col cols="12">
				<v-checkbox
					v-model="v.isBusinessman.$model"
					:disabled="disabled"
					label="Предыдущий кредитор является индивидуальным предпринимателем"
					hide-details
				/>
			</v-col>
			<template v-if="v.isBusinessman.$model">
				<v-col cols="6">
					<v-text-field
						v-model="v.ogrn.$model"
						:errorMessages="getValidationErrorText(v.ogrn)"
						:disabled="disabled"
						label="ОГРНИП"
						v-mask="['###############']"
						outlined
						@blur="v.ogrn.$touch"
					/>
				</v-col>
				<v-col cols="6">
					<v-text-field
						v-model="v.emailUl.$model"
						:errorMessages="getValidationErrorText(v.emailUl)"
						:disabled="disabled"
						label="Адрес электронной почты, содержащийся в ЕГРИП"
						outlined
						@blur="v.emailUl.$touch"
					/>
				</v-col>
			</template>
		</v-row>
		<v-divider class="divider"></v-divider>
		<v-row>
			<v-col cols="12">
				<v-text-field
					v-model="v.changeReason.$model"
					:errorMessages="getValidationErrorText(v.changeReason)"
					:disabled="disabled"
					label="Причина изменения кредитора"
					outlined
					@input="$emit('change-reason', $event)"
					@blur="v.changeReason.$touch"
				/>
			</v-col>
		</v-row>
	</form>
</template>

<script>
import deepmerge from 'deepmerge';
import {
	and,
	maxLength,
	minLength,
	required,
	requiredIf,
} from 'vuelidate/lib/validators';

import DateFieldCustom from '@/components/base/DateFieldCustom';
import AddressStructuredField, {
	addressValidate,
} from '@/components/base/AddressStructuredField';
import {
	EMAIL,
	getValidationErrorText,
	MAX_LENGTH,
	MAX_LENGTH_EMAIL,
	DATA_MATCH,
	DATE_FORMAT,
	DATE_FUTURE,
	DATE_PAST,
	MinLength,
	EMOJI,
	INN,
	PERSON_NAME, TRAILING_AND_LEADING_SPACES,
} from '@/utils/validate';

export const personModel = {
	lastName: '',
	firstName: '',
	patronymic: '',
	birthDate: '',
	address: {},
	snils: '',
	inn: '',
	isBusinessman: false,
	ogrn: '',
	emailUl: '',
	changeReason: '',
};

export const getPersonValidation = (applicant) => {
	let personValidation = {
		lastName: {
			required,
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES
		},
		firstName: {
			required,
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES
		},
		patronymic: {
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES
		},
		birthDate: {
			required,
			DATE: and(DATE_FORMAT, DATE_FUTURE, DATE_PAST),
		},
		address: {
			required,
			...addressValidate(),
		},
		snils: {
			required,
			minLength: minLength(MinLength.SNILS),
		},
		inn: {
			required,
			minLength: minLength(12),
			INN: INN(12),
		},
		isBusinessman: {},
		ogrn: {
			required: requiredIf('isBusinessman'),
			minLength: minLength(15),
		},
		emailUl: {
			EMAIL,
			required: requiredIf('isBusinessman'),
			maxLength: maxLength(MAX_LENGTH_EMAIL),
			TRAILING_AND_LEADING_SPACES
		},
		changeReason: {
			EMOJI,
			TRAILING_AND_LEADING_SPACES
		},
	};

	if (applicant.person) {
		personValidation = deepmerge(personValidation, {
			snils: {
				DATA_MATCH: DATA_MATCH(
					applicant.person.snils,
					'заявитель и предыдущий кредитор'
				),
			},
			inn: {
				DATA_MATCH: DATA_MATCH(
					applicant.person.inn,
					'заявитель и предыдущий кредитор'
				),
			},
			ogrn: {
				DATA_MATCH: DATA_MATCH(
					applicant.person.ogrn,
					'заявитель и предыдущий кредитор'
				),
			},
		});
	}

	return personValidation;
};

export default {
	name: 'Person',
	props: {
		v: Object,
		disabled: Boolean,
		translationKey: String,
	},
	components: {
		DateFieldCustom,
		AddressStructuredField,
	},
	data: () => ({
		getValidationErrorText,
	}),
};
</script>
